import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';
import {
  RiUserLine, RiMailLine, RiPhoneLine, RiAlarmWarningLine,
  RiMapPinLine, RiBriefcaseLine, RiShieldUserLine, RiAtLine,
  RiSaveLine, RiCheckLine, RiErrorWarningLine, RiPencilLine,
  RiLockLine, RiCakeLine,
} from 'react-icons/ri';

const EMPLOYEE_EDITABLE = ['name', 'email_id', 'phone', 'emergency_contact', 'address', 'date_of_birth'];

export default function EmployeeProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    name: '', email_id: '', phone: '', emergency_contact: '', address: '', date_of_birth: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const loadProfile = () => {
    setLoading(true);
    api.get('/users/me')
      .then((res) => {
        const data = res.data?.user || res.data?.data || res.data;
        setProfile(data);
        setForm({
          name: data?.name || '',
          email_id: data?.email_id || '',
          phone: data?.phone || '',
          emergency_contact: data?.emergency_contact || '',
          address: data?.address || '',
          // date_of_birth comes back as an ISO string / date — keep only YYYY-MM-DD for the input
          date_of_birth: (data?.date_of_birth || '').slice(0, 10),
        });
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load profile.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadProfile(); }, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = EMPLOYEE_EDITABLE.reduce((acc, k) => {
        const v = (form[k] ?? '').trim();
        // date_of_birth is a real DATE column — send null when blank, never ''
        // (an empty string fails Postgres date parsing).
        acc[k] = k === 'date_of_birth' ? (v || null) : v;
        return acc;
      }, {});
      const res = await api.put('/users/me', payload);
      const updated = res.data?.user || res.data?.data || res.data;
      if (updated && typeof updated === 'object') setProfile((p) => ({ ...p, ...updated }));
      setSuccess('Profile updated successfully.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const initialsSource = profile?.name || user?.name || profile?.username || 'U';
  const initials = ((initialsSource.match(/[A-Za-z]+/g) || [initialsSource])
    .map((w) => w[0]).join('').toUpperCase().slice(0, 2)) || 'U';

  const isActive = profile?.is_active !== false;

  return (
    <div className="p-7 space-y-6 bg-[#F8FAFC] w-full max-w-7xl mx-auto">

      {/* ── Hero card ─────────────────────────────────────────────── */}
      <div className="relative bg-white border border-[#E5E7EB] rounded-3xl shadow-[0_10px_40px_rgba(15,23,42,0.08)] overflow-hidden">
        {/* Gradient banner */}
        <div
          className="relative h-36 w-full"
          style={{ background: 'linear-gradient(120deg,#0984e3 0%,#00b0ff 35%,#7c3aed 75%,#d500f9 100%)' }}
        >
          <div
            className="absolute -top-20 -right-16 w-72 h-72 rounded-full opacity-30 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)' }}
          />
          <div
            className="absolute -bottom-28 -left-16 w-80 h-80 rounded-full opacity-15 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(circle, #fff 0%, transparent 70%)' }}
          />
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 30%,#fff 1px,transparent 1.5px),radial-gradient(circle at 75% 70%,#fff 1px,transparent 1.5px)',
              backgroundSize: '32px 32px',
            }}
          />
          <span className="absolute top-5 right-5 text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/15 text-white border border-white/30 backdrop-blur-md flex items-center gap-1.5">
            <RiShieldUserLine size={12} />
            {profile?.role || user?.role || 'Employee'}
          </span>
        </div>

        {/* Stacked content: avatar on its own row, then name + meta below */}
        <div className="px-8 pb-7">
          {/* Avatar — overlaps banner, sits on its own row */}
          <div className="relative w-28 h-28 -mt-16 mb-4">
            <div
              className="w-full h-full rounded-3xl border-[5px] border-white flex items-center justify-center text-white text-4xl font-bold shadow-[0_12px_30px_rgba(9,132,227,0.35)]"
              style={{ background: 'linear-gradient(135deg,#0984e3 0%,#7c3aed 60%,#d500f9 100%)' }}
            >
              {initials}
            </div>
            <span
              className={`absolute bottom-1 right-1 w-5 h-5 rounded-full border-[3px] border-white shadow ${
                isActive ? 'bg-emerald-500' : 'bg-gray-400'
              }`}
              title={isActive ? 'Active' : 'Inactive'}
            />
          </div>

          {/* Name + position + meta pills (full width, no overlap with avatar) */}
          {loading ? (
            <div className="space-y-2.5">
              <div className="h-7 bg-[#F3F4F6] rounded w-2/5 animate-pulse" />
              <div className="h-4 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
            </div>
          ) : (
            <>
              <h2 className="text-[#0F172A] text-2xl font-bold tracking-tight truncate">
                {profile?.name || '—'}
              </h2>
              <p className="text-[#64748B] text-sm mt-1 truncate">
                {profile?.position || 'Position not set'}
                {profile?.username && (
                  <span className="text-[#94A3B8]"> · @{profile.username}</span>
                )}
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {profile?.email_id && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#475569] bg-[#F1F5F9] border border-[#E2E8F0] px-3 py-1.5 rounded-full">
                    <RiMailLine size={12} className="text-[#0984e3]" /> {profile.email_id}
                  </span>
                )}
                {profile?.phone && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#475569] bg-[#F1F5F9] border border-[#E2E8F0] px-3 py-1.5 rounded-full">
                    <RiPhoneLine size={12} className="text-[#0984e3]" /> {profile.phone}
                  </span>
                )}
                {profile?.address && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#475569] bg-[#F1F5F9] border border-[#E2E8F0] px-3 py-1.5 rounded-full max-w-[260px]">
                    <RiMapPinLine size={12} className="text-[#0984e3] flex-shrink-0" />
                    <span className="truncate">{profile.address}</span>
                  </span>
                )}
                <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-semibold ${
                  isActive
                    ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
                    : 'text-gray-500 bg-gray-100 border border-gray-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Admin-controlled (read-only) ──────────────────────────── */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-lg bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center">
            <RiLockLine size={15} className="text-[#64748B]" />
          </div>
          <div>
            <h3 className="text-[#0F172A] font-bold text-sm">Administered by Admin</h3>
            <p className="text-[#94A3B8] text-xs">Contact your administrator to change these.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ReadOnlyField icon={RiAtLine}         label="Username" value={profile?.username} loading={loading} accent="#0984e3" />
          <ReadOnlyField icon={RiBriefcaseLine}  label="Position" value={profile?.position} loading={loading} accent="#7c3aed" />
          <ReadOnlyField icon={RiShieldUserLine} label="Role"     value={profile?.role}     loading={loading} accent="#d500f9" />
        </div>
      </div>

      {/* ── Editable form ─────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[rgba(9,132,227,0.08)] border border-[rgba(9,132,227,0.2)] flex items-center justify-center">
              <RiPencilLine size={15} className="text-[#0984e3]" />
            </div>
            <div>
              <h3 className="text-[#0F172A] font-bold text-sm">Personal Details</h3>
              <p className="text-[#94A3B8] text-xs">These fields are editable by you.</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
            <RiErrorWarningLine size={16} className="flex-shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2">
            <RiCheckLine size={16} className="flex-shrink-0" /> {success}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field
            icon={RiUserLine} label="Full Name" id="name"
            value={form.name} onChange={handleChange('name')}
            placeholder="Your full name" disabled={loading}
          />
          <Field
            icon={RiMailLine} label="Email Address" id="email_id" type="email"
            value={form.email_id} onChange={handleChange('email_id')}
            placeholder="you@example.com" disabled={loading}
          />
          <Field
            icon={RiPhoneLine} label="Phone" id="phone" type="tel"
            value={form.phone} onChange={handleChange('phone')}
            placeholder="+91 90000 00000" disabled={loading}
          />
          <Field
            icon={RiAlarmWarningLine} label="Emergency Phone" id="emergency_contact" type="tel"
            value={form.emergency_contact} onChange={handleChange('emergency_contact')}
            placeholder="+91 90000 00000" disabled={loading}
          />
          <Field
            icon={RiCakeLine} label="Date of Birth" id="date_of_birth" type="date"
            value={form.date_of_birth} onChange={handleChange('date_of_birth')}
            disabled={loading}
          />
          <div className="sm:col-span-2">
            <label htmlFor="address" className="block text-sm font-semibold text-[#0F172A] mb-1.5">
              Address
            </label>
            <div className="relative">
              <RiMapPinLine className="absolute left-3.5 top-3 text-[#9CA3AF]" size={17} />
              <textarea
                id="address"
                rows={3}
                value={form.address}
                onChange={handleChange('address')}
                disabled={loading}
                placeholder="Street, city, state, postal code"
                className="input-field pl-10 pt-3 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F1F5F9]">
          <button
            type="button"
            onClick={loadProfile}
            disabled={loading || saving}
            className="px-5 py-2.5 rounded-xl border border-[#E5E7EB] text-[#374151] text-sm font-semibold hover:bg-[#F8FAFC] transition-colors cursor-pointer disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={loading || saving}
            className="btn btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                Saving…
              </>
            ) : (
              <><RiSaveLine size={16} /> Save Changes</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ icon: Icon, label, id, type = 'text', value, onChange, placeholder, disabled }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-[#0F172A] mb-1.5">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={17} />
        <input
          id={id}
          type={type}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          className="input-field pl-10"
        />
      </div>
    </div>
  );
}

function ReadOnlyField({ icon: Icon, label, value, loading, accent = '#0984e3' }) {
  return (
    <div className="group flex items-center gap-3 p-3.5 rounded-xl bg-gradient-to-br from-[#F8FAFC] to-white border border-[#E2E8F0] hover:border-[#CBD5E1] hover:shadow-sm transition-all">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${accent}12`, border: `1px solid ${accent}30` }}
      >
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[#94A3B8] text-[11px] font-semibold uppercase tracking-wider">{label}</p>
        {loading
          ? <div className="h-4 bg-[#F1F5F9] rounded w-24 animate-pulse mt-1" />
          : <p className="text-[#0F172A] text-sm font-semibold truncate mt-0.5">{value || '—'}</p>}
      </div>
    </div>
  );
}
